/**
 * Client-side RBAC matrix for the 3-tier role model (ADR 0029 §3b).
 *
 * The backend (Core) is the source of truth for authorization — every
 * Senviro page calls Core through `apps/api`, which enforces the same
 * rules with row-level security and partner-subtree filtering. This
 * matrix exists purely so the UI can hide/disable affordances the user
 * cannot exercise (e.g. don't render a "+ New building" button for a
 * read-only customer); it never replaces server-side enforcement.
 *
 * Keep this file the single canonical RBAC table for the web client.
 * Downstream waves rely on it:
 *   - W6 task UX uses `can('task', 'create')`
 *   - W10 admin uses `can('org', 'manage')`
 *
 * Roles
 * - `platform` — SustEvo super-admins; full access to everything.
 * - `partner`  — partner-tier admin; read/create/update/delete on most
 *                resources within their own subtree, plus org/ownership
 *                management for their own customers. Cannot delete
 *                partners (only platform can) and cannot create
 *                platform-tier orgs.
 * - `customer` — customer-tier user; read/create/update/delete on their
 *                own buildings/portfolios/evaluations/documents/tasks
 *                within their org. Read-only on org. Cannot create or
 *                modify orgs or ownerships (must request via partner).
 */

export type Role = 'platform' | 'partner' | 'customer';

export type Resource =
  | 'building'
  | 'portfolio'
  | 'org'
  | 'ownership'
  | 'evaluation'
  | 'document'
  | 'task'
  // W10-F1 — Senviro UI translation catalog admin.
  | 'admin.translations'
  // #1546 — Senviro measurement-unit catalog admin.
  | 'admin.measurementUnits'
  // #1545 — Senviro currency catalog admin.
  | 'admin.currencies'
  // #1547 — Senviro file/evidence tag admin.
  | 'admin.tags'
  // #1550 — global time-travel override (platform-admin only).
  | 'admin.timeTravel'
  // #1584 — per-org time-travel override (partner+ within subtree).
  | 'admin.timeTravel.org'
  // #1548 — EU Taxonomy benchmarks matrix (platform-admin writes).
  | 'admin.euTaxonomyBenchmarks'
  // #1549 — EU Taxonomy XLS regression harness (platform-only).
  | 'admin.euTaxonomyTestRuns';

export type Action = 'read' | 'create' | 'update' | 'delete' | 'manage';

type RoleMatrix = Record<Resource, ReadonlyArray<Action>>;

const PLATFORM_MATRIX: RoleMatrix = {
  building: ['read', 'create', 'update', 'delete', 'manage'],
  portfolio: ['read', 'create', 'update', 'delete', 'manage'],
  org: ['read', 'create', 'update', 'delete', 'manage'],
  ownership: ['read', 'create', 'update', 'delete', 'manage'],
  evaluation: ['read', 'create', 'update', 'delete', 'manage'],
  document: ['read', 'create', 'update', 'delete', 'manage'],
  task: ['read', 'create', 'update', 'delete', 'manage'],
  'admin.translations': ['read', 'create', 'update', 'delete', 'manage'],
  'admin.measurementUnits': ['read', 'create', 'update', 'delete', 'manage'],
  'admin.currencies': ['read', 'create', 'update', 'delete', 'manage'],
  'admin.tags': ['read', 'create', 'update', 'delete', 'manage'],
  // Platform-only — partner_admin must NOT be able to change the global clock.
  'admin.timeTravel': ['read', 'manage'],
  // Platform can manage every per-org override (full subtree visibility).
  'admin.timeTravel.org': ['read', 'manage'],
  // Platform-only — EU Taxonomy benchmarks affect every scoring run.
  'admin.euTaxonomyBenchmarks': ['read', 'create', 'update', 'delete', 'manage'],
  // Platform-only — the regression harness affects no production state but
  // surfaces internals not appropriate for partner-tier visibility.
  'admin.euTaxonomyTestRuns': ['read', 'create', 'manage'],
};

const PARTNER_MATRIX: RoleMatrix = {
  building: ['read', 'create', 'update', 'delete'],
  portfolio: ['read', 'create', 'update', 'delete'],
  // Partners can read/create/update their own customer orgs but cannot
  // delete partners (only platform) and cannot create platform-tier orgs.
  org: ['read', 'create', 'update'],
  ownership: ['read', 'create', 'update', 'delete'],
  evaluation: ['read', 'create', 'update', 'delete'],
  document: ['read', 'create', 'update', 'delete'],
  task: ['read', 'create', 'update', 'delete'],
  // Partners manage their own UI translation overrides.
  'admin.translations': ['read', 'create', 'update', 'delete', 'manage'],
  // Partners manage their measurement-unit catalog.
  'admin.measurementUnits': ['read', 'create', 'update', 'delete', 'manage'],
  // Partners manage their currency catalog (e.g. enable NOK + custom symbols).
  'admin.currencies': ['read', 'create', 'update', 'delete', 'manage'],
  // Partners manage tags within their org subtree.
  'admin.tags': ['read', 'create', 'update', 'delete', 'manage'],
  // Partners can READ the global override (so the banner renders for their
  // users when active) but cannot toggle it — that's a global-blast-radius
  // action that belongs to platform-admin alone.
  'admin.timeTravel': ['read'],
  // Partners CAN toggle per-org overrides for orgs in their subtree
  // (#1584). Backend enforces the subtree check; this matrix only gates
  // whether the UI affordance is rendered.
  'admin.timeTravel.org': ['read', 'create', 'update', 'delete', 'manage'],
  // Partners can READ the EU Taxonomy benchmarks (for their scoring
  // context) but cannot edit them — global numbers, platform-owned.
  'admin.euTaxonomyBenchmarks': ['read'],
  // Regression harness is platform-only — partners don't see it at all.
  'admin.euTaxonomyTestRuns': [],
};

const CUSTOMER_MATRIX: RoleMatrix = {
  building: ['read', 'create', 'update', 'delete'],
  portfolio: ['read', 'create', 'update', 'delete'],
  // Customers can read their own org but not modify it.
  org: ['read'],
  // Customers cannot create or modify ownerships — must go through Partner.
  ownership: ['read'],
  evaluation: ['read', 'create', 'update', 'delete'],
  document: ['read', 'create', 'update', 'delete'],
  task: ['read', 'create', 'update', 'delete'],
  // Customers cannot edit translations — partner-tier and above only.
  'admin.translations': [],
  // Customers cannot edit measurement units — partner-tier and above only.
  'admin.measurementUnits': [],
  // Customers cannot edit currencies — partner-tier and above only.
  'admin.currencies': [],
  // Customers can read their org's tags (used in filters) but cannot manage.
  'admin.tags': ['read'],
  // Customers can read the banner state but never manage the clock.
  'admin.timeTravel': ['read'],
  // Customers can read their org's override (so the banner reflects it)
  // but never toggle it — that's a partner-admin / platform-admin action.
  'admin.timeTravel.org': ['read'],
  // Customers can read benchmarks (transparency for their scoring) but never edit.
  'admin.euTaxonomyBenchmarks': ['read'],
  // Customers never see the regression harness.
  'admin.euTaxonomyTestRuns': [],
};

const MATRIX: Record<Role, RoleMatrix> = {
  platform: PLATFORM_MATRIX,
  partner: PARTNER_MATRIX,
  customer: CUSTOMER_MATRIX,
};

/**
 * `can(role, resource, action)` — returns true iff the role is allowed to
 * perform `action` on `resource`. Undefined-safe: an unknown role/resource
 * yields `false`.
 */
export function can(role: Role, resource: Resource, action: Action): boolean {
  const roleMatrix = MATRIX[role];
  if (!roleMatrix) return false;
  const allowed = roleMatrix[resource];
  if (!allowed) return false;
  return allowed.includes(action);
}

/** Exposed for testing and for downstream introspection. */
export const __test__ = { MATRIX };
